import { SignalBusinessHours } from '@provision/entity-types';
import { useMutation } from '@provision/graphql-client';
import { useCallback } from 'react';
import { DELETE_SIGNAL_BUSINESS_HOURS_QUERY } from 'src/graphql/entities/signal-business-hours/signal-business-hours.operations';
import { ConditionInput } from 'src/graphql/types';
import { useNotifications } from 'src/utils/hooks';

export interface DeleteSignalBusinessHoursMutation {
  deleteSignalBusinessHours: Pick<SignalBusinessHours, 'id'>[];
}

export type DeleteSignalBusinessHoursMutationParams = {
  conditions: ConditionInput[];
};

export const useDeleteSignalBusinessHours = () => {
  const { showErrorAlert } = useNotifications();
  const [deleteSignalBusinessHoursMutation, { loading: isDeleting }] = useMutation<
    DeleteSignalBusinessHoursMutation,
    DeleteSignalBusinessHoursMutationParams
  >(DELETE_SIGNAL_BUSINESS_HOURS_QUERY, {
    update(cache, { data }) {
      if (!data?.deleteSignalBusinessHours?.length) {
        return;
      }
      data.deleteSignalBusinessHours.forEach((item) => {
        const id = cache.identify({
          __typename: 'SignalBusinessHours',
          id: item.id,
        });
        cache.evict({ id });
      });
      cache.gc();
    },
    onError: (error) => {
      console.error(error);
      showErrorAlert('Failed to deleting Signal business hours, please try again');
    },
  });

  const deleteSignalBusinessHoursById = useCallback(
    async (ids: string[]) => {
      if (!ids.length) {
        return;
      }
      await deleteSignalBusinessHoursMutation({
        variables: {
          conditions: [
            {
              attribute: 'signalBusinessHoursId',
              operator: 'in',
              value: ids.join(','),
            },
          ],
        },
        optimisticResponse: {
          deleteSignalBusinessHours: ids.map((id) => ({
            __typename: 'SignalBusinessHours',
            id,
          })),
        },
      });
    },
    [deleteSignalBusinessHoursMutation],
  );

  return {
    deleteSignalBusinessHoursById,
    isDeleting,
  };
};
